import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowLeft, CheckCircle, XCircle, Clock, Star, User, MapPin, Trophy, BarChart3, MessageSquare, ThumbsUp, ThumbsDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import NominationAnalytics from '@/components/admin/NominationAnalytics';
import NominationResponseReview from '@/components/admin/NominationResponseReview';

interface Nomination {
  id: string;
  player_name: string;
  player_username: string | null;
  sport: string;
  position: string | null;
  school: string | null;
  state_province: string;
  nomination_reason: string;
  nominator_relationship: string | null;
  nominator_user_id: string | null;
  nominator_user_type: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export default function AdminNominations() {
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  useEffect(() => {
    loadNominations();
  }, []);

  const loadNominations = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from('player_nominations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNominations(data || []);
    } catch (err) {
      console.error('Error loading nominations:', err);
      toast.error('Failed to load nominations');
    } finally {
      setLoading(false);
    }
  };

  const updateNominationStatus = async (id: string, status: string) => {
    try {
      const nomination = nominations.find(n => n.id === id);
      
      const { error } = await (supabase as any)
        .from('player_nominations')
        .update({
          status,
          admin_notes: adminNotes[id] || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      // If approved and player has a username, send them a notification
      if (status === 'approved' && nomination?.player_username) {
        await sendNominationNotification(nomination);
      }

      toast.success(`Nomination ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      loadNominations();
    } catch (err) {
      console.error('Error updating nomination:', err);
      toast.error('Failed to update nomination');
    }
  };

  const sendNominationNotification = async (nomination: Nomination) => {
    try {
      // Find the nominated player's user ID
      const username = nomination.player_username?.replace('@', '');
      if (!username) return;

      // Check internal_users first
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('id')
        .eq('username', username)
        .eq('is_active', true)
        .maybeSingle();

      // Check profiles
      const { data: profileUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .maybeSingle();

      const recipientId = internalUser?.id || profileUser?.id;
      if (!recipientId) return;

      const nominatorName = nomination.nominator_user_id ? 'Someone' : 'An anonymous user';

      await supabase.from('notifications').insert({
        user_id: recipientId,
        user_type: internalUser ? 'internal' : 'supabase',
        to_user_id: recipientId,
        notification_type: 'nomination',
        category: 'nomination',
        title: '🌟 You\'ve Been Nominated!',
        message: `${nominatorName} nominated you for "${nomination.sport}" - "${nomination.nomination_reason.substring(0, 100)}..."`,
        action_url: `/my-nominations`,
        priority: 'high',
        requires_action: false,
        metadata: JSON.stringify({ nomination_id: nomination.id }),
      });
    } catch (err) {
      console.error('Error sending nomination notification:', err);
    }
  };

  const filtered = nominations.filter(n => {
    if (activeTab === 'pending') return n.status === 'pending';
    if (activeTab === 'approved') return n.status === 'approved';
    if (activeTab === 'rejected') return n.status === 'rejected';
    if (activeTab === 'analytics' || activeTab === 'responses') return false;
    return true;
  });

  const counts = {
    pending: nominations.filter(n => n.status === 'pending').length,
    approved: nominations.filter(n => n.status === 'approved').length,
    rejected: nominations.filter(n => n.status === 'rejected').length,
  };

  // Get nominator username for display
  const getNominatorDisplay = (nom: Nomination) => {
    if (!nom.nominator_user_id) return 'Anonymous';
    return nom.nominator_user_type === 'internal' ? `Internal User (${nom.nominator_user_id.substring(0, 8)}...)` : `User (${nom.nominator_user_id.substring(0, 8)}...)`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved': return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected': return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-4" onClick={() => navigate('/admin/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <Star className="h-7 w-7 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Player Nominations</h1>
          {counts.pending > 0 && (
            <Badge className="bg-yellow-600 text-lg px-3">{counts.pending} pending</Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap">
            <TabsTrigger value="pending">Pending ({counts.pending})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
            <TabsTrigger value="all">All ({nominations.length})</TabsTrigger>
            <TabsTrigger value="analytics" className="gap-1"><BarChart3 className="h-3.5 w-3.5" />Analytics</TabsTrigger>
            <TabsTrigger value="responses" className="gap-1"><MessageSquare className="h-3.5 w-3.5" />Thank You Responses</TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <NominationAnalytics nominations={nominations} />
          </TabsContent>

          {/* Responses Tab */}
          <TabsContent value="responses">
            <NominationResponseReview />
          </TabsContent>

          {/* Nomination listing tabs */}
          {['pending', 'approved', 'rejected', 'all'].includes(activeTab) && (
            <TabsContent value={activeTab} className="space-y-4">
              {loading ? (
                <Card><CardContent className="py-8 text-center text-muted-foreground">Loading nominations...</CardContent></Card>
              ) : filtered.length === 0 ? (
                <Card><CardContent className="py-8 text-center text-muted-foreground">No {activeTab} nominations found.</CardContent></Card>
              ) : (
                filtered.map(nom => (
                  <Card key={nom.id} className="border-border">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <User className="h-5 w-5 text-primary" />
                            {nom.player_name}
                            {nom.player_username && (
                              <span className="text-sm font-normal text-muted-foreground">{nom.player_username}</span>
                            )}
                          </CardTitle>
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" />{nom.sport}{nom.position && ` • ${nom.position}`}</span>
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{nom.state_province}</span>
                            {nom.school && <span>🏫 {nom.school}</span>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {getStatusBadge(nom.status || 'pending')}
                          {/* Show nomination count for this player */}
                          {(() => {
                            const count = nominations.filter(n => 
                              n.player_name === nom.player_name || 
                              (n.player_username && n.player_username === nom.player_username)
                            ).length;
                            return count > 1 ? (
                              <Badge variant="secondary" className="text-xs">
                                {count} total nominations
                              </Badge>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Nomination Reason:</p>
                        <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">{nom.nomination_reason}</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-muted/30 rounded-md text-sm">
                        <div>
                          <span className="font-medium text-foreground">Nominated by:</span>
                          <span className="ml-2 text-muted-foreground">{getNominatorDisplay(nom)}</span>
                        </div>
                        <div>
                          <span className="font-medium text-foreground">Submitted:</span>
                          <span className="ml-2 text-muted-foreground">{new Date(nom.created_at).toLocaleString()}</span>
                        </div>
                        {nom.nominator_relationship && (
                          <div>
                            <span className="font-medium text-foreground">Relationship:</span>
                            <span className="ml-2 text-muted-foreground">{nom.nominator_relationship}</span>
                          </div>
                        )}
                        {nom.nominator_user_type && (
                          <div>
                            <span className="font-medium text-foreground">Nominator type:</span>
                            <span className="ml-2 text-muted-foreground">{nom.nominator_user_type}</span>
                          </div>
                        )}
                        {nom.reviewed_at && (
                          <div>
                            <span className="font-medium text-foreground">Reviewed:</span>
                            <span className="ml-2 text-muted-foreground">{new Date(nom.reviewed_at).toLocaleString()}</span>
                          </div>
                        )}
                      </div>

                      {nom.status === 'pending' && (
                        <div className="pt-3 border-t border-border space-y-3">
                          <Textarea
                            placeholder="Admin notes (optional)..."
                            value={adminNotes[nom.id] || ''}
                            onChange={(e) => setAdminNotes(prev => ({ ...prev, [nom.id]: e.target.value }))}
                            className="text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updateNominationStatus(nom.id, 'approved')}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => updateNominationStatus(nom.id, 'rejected')}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}

                      {nom.admin_notes && nom.status !== 'pending' && (
                        <div className="pt-2">
                          <p className="text-xs font-medium text-foreground">Admin Notes:</p>
                          <p className="text-xs text-muted-foreground">{nom.admin_notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          )}
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
